import React, { useEffect, useState, useCallback } from "react";
import "../styles/AdminDonations.css";
import { DONATIONS_API, API_BASE, getImageUrl } from "../config/api";
import { showConfirm, showPrompt } from "../utils/customDialog";

function AdminDonations() {
  const [streamFilter, setStreamFilter] = useState("all"); // "all" | "vaithikasri" | "gurukulam"
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "pending" | "verified" | "rejected"
  const [searchQuery, setSearchQuery] = useState("");
  const [donationsList, setDonationsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState("");

  const showPopup = (msg) => {
    setCopyFeedback(msg);
    setTimeout(() => setCopyFeedback(""), 3500);
  };

  const getAdminHeaders = () => ({
    "x-admin-auth": localStorage.getItem("adminAuth") || "true",
    "x-admin-email": localStorage.getItem("adminEmail") || "",
    "x-admin-id": localStorage.getItem("adminId") || "",
  });

  /* ================= FETCH ALL DONATIONS ================= */
  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(DONATIONS_API, {
        headers: {
          "x-admin-auth": localStorage.getItem("adminAuth") || "true",
          "x-admin-email": localStorage.getItem("adminEmail") || "",
          "x-admin-id": localStorage.getItem("adminId") || "",
        },
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.status === "success") {
          setDonationsList(Array.isArray(data.data) ? data.data : []);
        }
      }
    } catch (err) {
      console.error("Fetch donations error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 8000);
    return () => clearInterval(interval);
  }, [fetchData]);

  /* ================= HELPER: STREAM DETECTION ================= */
  const isGurukulamDonation = (item) => {
    const dt = (item.donation_type || "").toLowerCase();
    const pm = (item.payment_mode || "").toLowerCase();
    const notes = (item.other_reason || "").toLowerCase();
    return (
      dt.includes("seetharama") ||
      dt.includes("gurukulam") ||
      dt.includes("trust") ||
      dt.includes("samradhana") ||
      pm.includes("enquiry") ||
      notes.includes("seetharama") ||
      notes.includes("gurukulam")
    );
  };

  /* ================= VERIFY DONATION / ACKNOWLEDGE PLEDGE ================= */
  const verifyDonation = async (item) => {
    const isGurukulam = isGurukulamDonation(item);
    const id = item.id;
    try {
      setActionLoadingId(id);
      const res = await fetch(`${DONATIONS_API}/${id}/verify`, {
        method: "PUT",
        headers: {
          ...getAdminHeaders(),
          "Content-Type": "application/json",
        },
      });
      const data = await res.json();
      if (data?.status === "success") {
        showPopup(
          isGurukulam
            ? `<i class="bi bi-patch-check-fill" style="margin-right: 8px; color: #10b981;"></i> Pledge Acknowledged! Ref ${data.receipt_number} generated & emailed.`
            : `<i class="bi bi-patch-check-fill" style="margin-right: 8px; color: #10b981;"></i> Verified! Receipt ${data.receipt_number} generated & emailed.`
        );
        fetchData();
      } else {
        showPopup(`<i class="bi bi-exclamation-circle" style="margin-right: 8px; color: #ef4444;"></i> ${data.message || "Failed to verify record"}`);
      }
    } catch (err) {
      console.error("Verify error:", err);
      showPopup(`<i class="bi bi-exclamation-circle" style="margin-right: 8px; color: #ef4444;"></i> Server error`);
    } finally {
      setActionLoadingId(null);
    }
  };

  /* ================= REJECT DONATION ================= */
  const rejectDonation = async (id) => {
    const reason = await showPrompt({
      title: "Reject Donation",
      message: "Please enter a reason for rejecting this donation record:",
      defaultValue: "Payment proof mismatch or incomplete details",
      confirmText: "Reject Donation"
    });
    if (reason === null) return;

    try {
      setActionLoadingId(id);
      const res = await fetch(`${DONATIONS_API}/${id}/reject`, {
        method: "PUT",
        headers: {
          ...getAdminHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ reason }),
      });
      const data = await res.json();
      if (data?.status === "success") {
        showPopup(`<i class="bi bi-x-circle-fill" style="margin-right: 8px; color: #ef4444;"></i> Donation rejected`);
        fetchData();
      } else {
        showPopup(`<i class="bi bi-exclamation-circle" style="margin-right: 8px; color: #ef4444;"></i> ${data.message || "Failed to reject donation"}`);
      }
    } catch (err) {
      console.error("Reject error:", err);
      showPopup(`<i class="bi bi-exclamation-circle" style="margin-right: 8px; color: #ef4444;"></i> Server error`);
    } finally {
      setActionLoadingId(null);
    }
  };

  /* ================= DELETE DONATION RECORD ================= */
  const deleteDonationRecord = async (id) => {
    const ok = await showConfirm({
      title: "Delete Donation Record",
      message: "Are you sure you want to delete this donation record?",
      subMessage: "This will permanently remove the record from donation history.",
      confirmText: "Delete Record",
      type: "danger"
    });
    if (!ok) return;

    try {
      setActionLoadingId(id);
      const res = await fetch(`${DONATIONS_API}/${id}`, {
        method: "DELETE",
        headers: getAdminHeaders(),
      });
      const data = await res.json();
      if (data?.status === "success") {
        setDonationsList((prev) => prev.filter((item) => item.id !== id));
        showPopup(`<i class="bi bi-trash" style="margin-right: 8px; color: #ef4444;"></i> Donation record deleted`);
      } else {
        showPopup(`<i class="bi bi-exclamation-circle" style="margin-right: 8px; color: #ef4444;"></i> Delete failed`);
      }
    } catch (err) {
      console.error("Delete error:", err);
      showPopup(`<i class="bi bi-exclamation-circle" style="margin-right: 8px; color: #ef4444;"></i> Server error`);
    } finally {
      setActionLoadingId(null);
    }
  };

  /* ================= COPY TO CLIPBOARD ================= */
  const handleCopy = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    showPopup(`<i class="bi bi-check2-circle" style="margin-right: 8px; color: #10b981;"></i> Copied ${label} to clipboard!`);
  };

  const getInitials = (name) => {
    if (!name) return "D";
    const parts = name.trim().split(" ");
    if (parts.length > 1) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  };

  /* ================= HELPER: PROOF DISCRIMINATOR ================= */
  const parsePaymentProof = (proof) => {
    if (!proof) return null;
    const str = String(proof).trim();
    if (!str) return null;

    // Check if it's a file path uploaded to backend
    const hasPath = str.includes("/") || str.includes("\\") || str.startsWith("uploads");
    const hasExt = /\.(jpg|jpeg|png|webp|gif|pdf)$/i.test(str);
    const isRazorpayText = str.toLowerCase().startsWith("razorpay:") || str.toLowerCase().startsWith("pay_");

    if ((hasPath || hasExt) && !isRazorpayText) {
      const fileUrl = getImageUrl(str);
      return { type: "file", url: fileUrl, label: str };
    }

    // It's a text Razorpay Payment ID or reference
    const cleanId = str.replace(/^razorpay:\s*/i, "").trim();
    return { type: "razorpay_id", id: cleanId };
  };

  /* ================= COUNTS & FILTERING ================= */
  const vaithikasriList = donationsList.filter((d) => !isGurukulamDonation(d));
  const gurukulamList = donationsList.filter((d) => isGurukulamDonation(d));

  const activeStreamList =
    streamFilter === "vaithikasri"
      ? vaithikasriList
      : streamFilter === "gurukulam"
      ? gurukulamList
      : donationsList;

  const filteredDonations = activeStreamList.filter((item) => {
    if (statusFilter !== "all" && item.status !== statusFilter) {
      return false;
    }
    const query = searchQuery.toLowerCase().trim();
    if (!query) return true;

    return (
      (item.donor_name || "").toLowerCase().includes(query) ||
      (item.contact_number || "").toLowerCase().includes(query) ||
      (item.email || "").toLowerCase().includes(query) ||
      (item.receipt_number || "").toLowerCase().includes(query) ||
      (item.donation_type || "").toLowerCase().includes(query) ||
      (item.payment_mode || "").toLowerCase().includes(query) ||
      (item.pan_number || "").toLowerCase().includes(query) ||
      (item.aadhaar_number || "").toLowerCase().includes(query) ||
      (item.subscription_number || "").toLowerCase().includes(query)
    );
  });

  const totalVerifiedAmount = donationsList
    .filter((d) => d.status === "verified")
    .reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);

  return (
    <div className="admin-donations-container admin-portal-root">
      {/* Toast Notification */}
      {copyFeedback && (
        <div
          className="admin-toast-popup"
          dangerouslySetInnerHTML={{ __html: copyFeedback }}
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            background: "#1e293b",
            color: "#ffffff",
            padding: "12px 22px",
            borderRadius: "10px",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
            fontSize: "13.5px",
            fontWeight: "600",
          }}
        />
      )}

      {/* Header Banner */}
      <div className="donations-header">
        <h2>
          <i className="bi bi-wallet2" style={{ color: "#d4af37" }}></i> Donation Management Center
        </h2>
        <p>
          Verify incoming donations for <strong>Vaithikasri Publications LLP</strong> (Razorpay / UPI) and <strong>Seetharama Gurukulam Trust</strong> (Pledges & Enquiries)
        </p>
      </div>

      {/* STATS OVERVIEW CARDS */}
      <div className="donations-stats-grid">
        <div className="donation-stat-card verified-total">
          <div className="stat-label">Total Verified Collections</div>
          <div className="stat-value maroon">
            ₹{totalVerifiedAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </div>
        </div>

        <div className="donation-stat-card total-count">
          <div className="stat-label">Total Submissions</div>
          <div className="stat-value">{donationsList.length}</div>
        </div>

        <div className="donation-stat-card pending-count">
          <div className="stat-label">Pending Verifications</div>
          <div className="stat-value pending">
            {donationsList.filter((d) => d.status === "pending").length}
          </div>
        </div>

        <div className="donation-stat-card issued-count">
          <div className="stat-label">Verified & Issued</div>
          <div className="stat-value success">
            {donationsList.filter((d) => d.status === "verified").length}
          </div>
        </div>
      </div>

      {/* FILTER & SEARCH CARD */}
      <div className="donations-controls-card">
        {/* Stream Selector Tabs */}
        <div className="stream-tabs-row">
          <button
            type="button"
            className={`stream-tab-pill ${streamFilter === "all" ? "active" : ""}`}
            onClick={() => setStreamFilter("all")}
          >
            <i className="bi bi-collection-fill"></i> All Donations ({donationsList.length})
          </button>

          <button
            type="button"
            className={`stream-tab-pill ${streamFilter === "vaithikasri" ? "active" : ""}`}
            onClick={() => setStreamFilter("vaithikasri")}
          >
            <i className="bi bi-journal-richtext"></i> Vaithikasri Publications ({vaithikasriList.length})
          </button>

          <button
            type="button"
            className={`stream-tab-pill ${streamFilter === "gurukulam" ? "active" : ""}`}
            onClick={() => setStreamFilter("gurukulam")}
          >
            <i className="bi bi-mortarboard-fill"></i> Seetharama Gurukulam Trust ({gurukulamList.length})
          </button>
        </div>

        {/* Status Pills & Search Row */}
        <div className="status-filter-row">
          <div className="status-pills-group">
            <span style={{ fontWeight: "700", fontSize: "13px", color: "#64748b", alignSelf: "center", marginRight: "4px" }}>Filter:</span>
            {["all", "pending", "verified", "rejected"].map((st) => (
              <button
                key={st}
                type="button"
                className={`status-pill-btn ${statusFilter === st ? "active" : ""}`}
                onClick={() => setStatusFilter(st)}
              >
                {st === "all"
                  ? `All (${activeStreamList.length})`
                  : `${st.charAt(0).toUpperCase() + st.slice(1)} (${activeStreamList.filter((d) => d.status === st).length})`}
              </button>
            ))}
          </div>

          <div className="search-input-wrapper">
            <i className="bi bi-search"></i>
            <input
              type="text"
              className="search-input-field admin-search-input"
              style={{ paddingLeft: "48px" }}
              placeholder="Search donor, phone, email, sub ID, receipt..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* CONTENT LIST GRID */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-danger me-2" role="status"></div>
          <span style={{ color: "#64748b", fontWeight: "600" }}>Loading donations...</span>
        </div>
      ) : filteredDonations.length === 0 ? (
        <div className="donations-empty-card">
          <i className="bi bi-folder-x"></i>
          <h4>No donation records found</h4>
          <p>Try refining your search query or status filter.</p>
        </div>
      ) : (
        <div className="donations-cards-grid">
          {filteredDonations.map((item) => {
            const isGurukulam = isGurukulamDonation(item);
            const proofData = parsePaymentProof(item.payment_proof);
            const cleanBase = API_BASE.endsWith("/") ? API_BASE.slice(0, -1) : API_BASE;
            const pdfReceiptUrl = `${cleanBase}/api/donations/${item.id}/receipt`;

            return (
              <div
                key={item.id}
                className={`donation-item-card ${isGurukulam ? "gurukulam-card" : "vaithikasri-card"}`}
              >
                <div>
                  {/* Card Header */}
                  <div className="card-top-header">
                    <div
                      className="donor-avatar-circle"
                      style={{ background: isGurukulam ? "#e67e22" : "#5d1d2b" }}
                    >
                      {getInitials(item.donor_name)}
                    </div>
                    <div className="donor-header-info">
                      <h3 className="donor-name-text">{item.donor_name}</h3>
                      <p className="donor-date-text">
                        <i className="bi bi-calendar3 me-1"></i>
                        {new Date(item.created_at).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                    <div className="card-badges-column">
                      <span className={`status-badge-pill ${item.status}`}>
                        {item.status}
                      </span>
                      <span className={`stream-type-tag ${isGurukulam ? "gurukulam" : "vaithikasri"}`}>
                        {isGurukulam ? "Gurukulam Trust" : "Vaithikasri Pub."}
                      </span>
                    </div>
                  </div>

                  {/* Highlight Amount Banner */}
                  <div className="donation-amount-banner">
                    <span className="amount-title-lbl">
                      {isGurukulam ? "Pledged Amount:" : "Amount Paid:"}
                    </span>
                    <span className="amount-value-num">
                      ₹{parseFloat(item.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </span>
                  </div>

                  {/* Reference Ribbon if Verified */}
                  {item.receipt_number && (
                    <div className="receipt-number-ribbon">
                      <span>
                        <i className="bi bi-patch-check-fill me-1"></i> {isGurukulam ? "Pledge Ref:" : "Receipt:"} {item.receipt_number}
                      </span>
                      <button
                        className="copy-mini-btn"
                        onClick={() => handleCopy(item.receipt_number, isGurukulam ? "Pledge Reference" : "Receipt Number")}
                        title={isGurukulam ? "Copy Pledge Reference" : "Copy Receipt Number"}
                      >
                        <i className="bi bi-clipboard"></i>
                      </button>
                    </div>
                  )}

                  {/* Details List */}
                  <div className="donation-details-list">
                    <div className="detail-item-row">
                      <span className="detail-label-title"><i className="bi bi-tag-fill me-1"></i> Purpose:</span>
                      <span className="detail-value-content" style={{ color: "#5d1d2b", fontWeight: "700" }}>
                        {item.donation_type}
                      </span>
                    </div>

                    <div className="detail-item-row">
                      <span className="detail-label-title"><i className="bi bi-credit-card me-1"></i> Mode:</span>
                      <span className="detail-value-content">{item.payment_mode || (isGurukulam ? "Enquiry / Direct Office Follow-up" : "Online / UPI")}</span>
                    </div>

                    <div className="detail-item-row">
                      <span className="detail-label-title"><i className="bi bi-telephone me-1"></i> Phone:</span>
                      <span className="detail-value-content">
                        {item.contact_number}
                        <button className="copy-mini-btn" onClick={() => handleCopy(item.contact_number, "Phone")} title="Copy Phone">
                          <i className="bi bi-clipboard"></i>
                        </button>
                      </span>
                    </div>

                    <div className="detail-item-row">
                      <span className="detail-label-title"><i className="bi bi-envelope me-1"></i> Email:</span>
                      <span className="detail-value-content">
                        {item.email}
                        <button className="copy-mini-btn" onClick={() => handleCopy(item.email, "Email")} title="Copy Email">
                          <i className="bi bi-clipboard"></i>
                        </button>
                      </span>
                    </div>

                    {item.subscription_number && (
                      <div className="detail-item-row">
                        <span className="detail-label-title"><i className="bi bi-person-badge me-1"></i> Sub ID:</span>
                        <span className="detail-value-content" style={{ color: "#5d1d2b", fontWeight: "800" }}>
                          {item.subscription_number}
                        </span>
                      </div>
                    )}

                    {item.address && (
                      <div className="detail-item-row">
                        <span className="detail-label-title"><i className="bi bi-geo-alt me-1"></i> Address:</span>
                        <span className="detail-value-content">{item.address}</span>
                      </div>
                    )}

                    {item.pan_number && (
                      <div className="detail-item-row">
                        <span className="detail-label-title"><i className="bi bi-card-text me-1"></i> PAN:</span>
                        <span className="detail-value-content">{item.pan_number}</span>
                      </div>
                    )}

                    {item.aadhaar_number && (
                      <div className="detail-item-row">
                        <span className="detail-label-title"><i className="bi bi-person-vcard me-1"></i> Aadhaar:</span>
                        <span className="detail-value-content">{item.aadhaar_number}</span>
                      </div>
                    )}

                    {item.other_reason && (
                      <div className="detail-item-row">
                        <span className="detail-label-title"><i className="bi bi-chat-left-text me-1"></i> Notes:</span>
                        <span className="detail-value-content" style={{ fontSize: "12px", color: "#475569" }}>
                          {item.other_reason}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Payment Proof / Razorpay ID Card */}
                  {proofData && (
                    <div className="proof-attachment-card">
                      <div className="proof-attachment-title">
                        <i className="bi bi-paperclip"></i> Payment Verification Record
                      </div>
                      {proofData.type === "file" ? (
                        <button
                          type="button"
                          className="btn-view-proof-file"
                          onClick={() => window.open(proofData.url, "_blank")}
                        >
                          <i className="bi bi-file-image"></i> View Attached Proof File
                        </button>
                      ) : (
                        <div className="proof-razorpay-badge">
                          <span><i className="bi bi-shield-check me-1" style={{ color: "#1d4ed8" }}></i> Razorpay Ref: <strong>{proofData.id}</strong></span>
                          <button
                            className="copy-mini-btn"
                            onClick={() => handleCopy(proofData.id, "Razorpay Payment ID")}
                            title="Copy Razorpay Payment ID"
                          >
                            <i className="bi bi-clipboard"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Redesigned Sleek Action Buttons Row */}
                <div className="donation-card-actions">
                  {item.status === "pending" && (
                    <>
                      <button
                        type="button"
                        className="btn-action-verify"
                        disabled={actionLoadingId === item.id}
                        onClick={() => verifyDonation(item)}
                      >
                        <i className="bi bi-check-circle-fill"></i> {isGurukulam ? "Acknowledge & Register Pledge" : "Verify & Issue Receipt"}
                      </button>
                      <button
                        type="button"
                        className="btn-action-reject"
                        disabled={actionLoadingId === item.id}
                        onClick={() => rejectDonation(item.id)}
                      >
                        <i className="bi bi-x-circle-fill"></i> Reject
                      </button>
                    </>
                  )}

                  {item.status === "verified" && (
                    <button
                      type="button"
                      className="btn-action-receipt"
                      onClick={() => window.open(pdfReceiptUrl, "_blank")}
                    >
                      <i className="bi bi-file-earmark-pdf-fill"></i> {isGurukulam ? "Download Pledge Acknowledgement" : "Download PDF Receipt"}
                    </button>
                  )}

                  <button
                    type="button"
                    className="btn-action-delete"
                    disabled={actionLoadingId === item.id}
                    onClick={() => deleteDonationRecord(item.id)}
                    title="Delete Record"
                  >
                    <i className="bi bi-trash3"></i>
                  </button>
                </div>

              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AdminDonations;
